// AI-voiced TTS via the server. Plays mp3 audio returned from /api/tts
// (ElevenLabs eleven_turbo_v2_5 by default; OpenAI fallback). The voice
// identifier is the elf slug (santa, peppermint, coalie, …) — the server
// maps that to the right ElevenLabs voice ID and settings.
//
// Playback uses a FIFO queue: speakAs() enqueues, a single processor plays
// one clip at a time and waits for `ended` before starting the next. New
// enqueues NEVER interrupt the current clip. Use skipCurrent() to skip the
// active clip from a UI button.

export interface SpeechHandle {
  stop: () => void;
}

// --- Diagnostics ---
// Every [jingle-audio] event flows through audioLog() so it both shows up
// in the dev console AND fires a CustomEvent the on-screen <AudioDiagnostic />
// component can subscribe to. Without this, debugging on a real iPhone is
// impossible — Safari Mobile gives no console access without a tethered Mac.
export interface JingleAudioLogDetail {
  ts: number;
  event: string;
  detail: string;
  level: "info" | "warn";
}

function safeStringify(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (v instanceof Error) return v.message || String(v);
  try { return JSON.stringify(v); } catch { return String(v); }
}

function audioLog(event: string, detail: unknown = "", level: "info" | "warn" = "info"): void {
  const detailStr = safeStringify(detail);
  const tag = "[jingle-audio]";
  if (level === "warn") {
    if (detailStr) console.warn(tag, event, detail);
    else console.warn(tag, event);
  } else {
    if (detailStr) console.log(tag, event, detail);
    else console.log(tag, event);
  }
  if (typeof window !== "undefined") {
    try {
      const payload: JingleAudioLogDetail = {
        ts: Date.now(),
        event,
        detail: detailStr,
        level,
      };
      window.dispatchEvent(new CustomEvent("jingle-audio-log", { detail: payload }));
    } catch {
      // Event dispatch is purely advisory — never let it break playback.
    }
  }
}

// In-memory cache of decoded audio blob URLs per (voice|text) so replays are
// instant after the first fetch.
const blobCache = new Map<string, string>();

function cacheKey(voice: string, text: string, direction = ""): string {
  return voice + "::" + direction + "::" + text;
}

// V3+ source-aware cache key. Different namespace from the text-based key
// because the source-aware route resolves text server-side — two callers
// with the same sourceType+sourceId share audio even if they pass slightly
// different display text on the client.
function sourceCacheKey(
  sourceType: string,
  sourceId: number,
  voice: string,
): string {
  return `src::${sourceType}::${sourceId}::${voice}`;
}

// Surfaced from postTts when the per-tenant daily voice cap (V4) trips.
// The button surface listens for this kind to render the "voices resting"
// state with a Retry-After-derived countdown, instead of looking like the
// playback simply died.
export interface SpeechRateLimitedError extends Error {
  kind: "rate-limited";
  retryAfterSeconds: number;
}
export function isSpeechRateLimitedError(
  err: unknown,
): err is SpeechRateLimitedError {
  return (
    err != null &&
    typeof err === "object" &&
    (err as { kind?: string }).kind === "rate-limited"
  );
}

function makeRateLimitedError(retryAfterSeconds: number): SpeechRateLimitedError {
  const err = new Error("voice_daily_cap_exceeded") as SpeechRateLimitedError;
  err.kind = "rate-limited";
  err.retryAfterSeconds = Math.max(1, Math.floor(retryAfterSeconds));
  return err;
}

function parseRetryAfter(headerValue: string | null): number {
  if (!headerValue) return 60;
  const n = Number(headerValue);
  if (Number.isFinite(n) && n > 0) return Math.floor(n);
  // Fallback: attempt HTTP-date parse. On failure, default to 1 minute so
  // the UI surfaces a wait period even if the header is malformed.
  const ms = Date.parse(headerValue);
  if (Number.isFinite(ms)) {
    return Math.max(1, Math.floor((ms - Date.now()) / 1000));
  }
  return 60;
}

async function postTts(
  voice: string,
  text: string,
  direction: string,
): Promise<string> {
  const key = cacheKey(voice, text, direction);
  const res = await fetch("/api/tts", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ voice, text, direction }),
    credentials: "include",
  });
  if (res.status === 429) {
    throw makeRateLimitedError(parseRetryAfter(res.headers.get("Retry-After")));
  }
  if (!res.ok) throw new Error("TTS HTTP " + res.status);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  blobCache.set(key, url);
  return url;
}

// V3+ source-aware fetch. The server resolves the text from the
// (sourceType, sourceId) tuple, so the client ONLY sends the source
// identifier and an optional preferred voice. Same blob-cache contract as
// the legacy postTts so subsequent taps on the same source are instant.
async function postTtsSource(
  source: VoiceSource,
  voice: string,
): Promise<string> {
  const key = sourceCacheKey(source.sourceType, source.sourceId, voice);
  const body: { sourceType: string; sourceId: number; voice?: string } = {
    sourceType: source.sourceType,
    sourceId: source.sourceId,
  };
  // Only forward `voice` when the caller has explicitly chosen one — the
  // server prefers its own resolved character slug, so omitting the field
  // avoids spurious 422 voice_character_mismatch on stale clients.
  if (voice && voice !== "alloy") body.voice = voice;
  const res = await fetch("/api/tts/source", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  if (res.status === 429) {
    throw makeRateLimitedError(parseRetryAfter(res.headers.get("Retry-After")));
  }
  if (!res.ok) {
    const errBody = await res.text().catch(() => "");
    throw new Error("TTS source HTTP " + res.status + (errBody ? `: ${errBody}` : ""));
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  blobCache.set(key, url);
  return url;
}

async function fetchAudio(voice: string, text: string, direction = ""): Promise<string> {
  const key = cacheKey(voice, text, direction);
  const hit = blobCache.get(key);
  // True blob URLs are guaranteed-playable. Return immediately.
  if (hit && hit.startsWith("blob:")) return hit;
  // HTTP cache hit (from server prefetch via registerAudioUrl). The server
  // key may be stale (e.g. cache-version bump after a deploy → 404). Try to
  // download into a blob first; on ANY failure, regenerate via POST /tts so
  // playback never silently dies on a stale key.
  if (hit) {
    try {
      return await downloadToBlob(key, hit);
    } catch (err) {
      audioLog("cache-stale", err, "warn");
    }
  }
  return await postTts(voice, text, direction);
}

async function fetchAudioForSource(
  source: VoiceSource,
  voice: string,
): Promise<string> {
  const key = sourceCacheKey(source.sourceType, source.sourceId, voice);
  const hit = blobCache.get(key);
  if (hit && hit.startsWith("blob:")) return hit;
  return await postTtsSource(source, voice);
}

// Download bytes for a URL (HTTP) and stash them as a blob: URL in the cache,
// replacing whatever was there. Returns the blob: URL.
async function downloadToBlob(
  key: string,
  httpUrl: string,
): Promise<string> {
  const res = await fetch(httpUrl, { credentials: "include" });
  if (!res.ok) throw new Error("prefetch HTTP " + res.status);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  blobCache.set(key, url);
  return url;
}

// Allow callers to register a precomputed audio URL (from a server prefetch)
// so we don't refetch when the user taps Play.
export function registerAudioUrl(voice: string, text: string, url: string, direction = "") {
  if (!voice || !text || !url) return;
  blobCache.set(cacheKey(voice, text, direction), url);
}

// One shared <audio> element that gets unlocked on the first user gesture.
// iOS/Safari refuses to play sound from an Audio() created programmatically
// long after the gesture; reusing a single element that was play()-ed once
// during the gesture sidesteps this entirely.
let sharedAudio: HTMLAudioElement | null = null;
// Set to true the moment a real clip's src has been assigned. Used by the
// unlock handler to know when its post-play cleanup would clobber a real
// playback that started in the same click handler.
let realClipQueued = false;
// Promise that resolves once the iOS unlock attempt finishes. The queue
// processor awaits this so src-assignment is deterministically ordered
// AFTER the unlock's pause/load cleanup, never before it.
let unlockDone: Promise<void> = Promise.resolve();
export function getSharedAudio(): HTMLAudioElement {
  if (sharedAudio) return sharedAudio;
  const a = new Audio();
  a.preload = "auto";
  // Intentionally NOT setting crossOrigin — these are same-origin URLs and
  // CORS mode strips cookies the server uses to authenticate the request.
  sharedAudio = a;
  return a;
}

// --- Word-timing fetch (for SpokenText) ---
export interface WordTiming { word: string; start: number; end: number; }
const timingsCache = new Map<string, WordTiming[] | null>();
const timingsInflight = new Map<string, Promise<WordTiming[] | null>>();

export async function fetchTimings(timingsUrl: string): Promise<WordTiming[] | null> {
  if (!timingsUrl) return null;
  if (timingsCache.has(timingsUrl)) return timingsCache.get(timingsUrl)!;
  const inflight = timingsInflight.get(timingsUrl);
  if (inflight) return inflight;
  const p = (async () => {
    try {
      const res = await fetch(timingsUrl, { credentials: "include" });
      if (!res.ok) {
        audioLog("timings-failed", "HTTP " + res.status, "warn");
        timingsCache.set(timingsUrl, null);
        return null;
      }
      const json = (await res.json()) as { timings: WordTiming[] | null };
      const t = Array.isArray(json.timings) ? json.timings : null;
      timingsCache.set(timingsUrl, t);
      return t;
    } catch (err) {
      audioLog("timings-failed", err, "warn");
      timingsCache.set(timingsUrl, null);
      return null;
    } finally {
      timingsInflight.delete(timingsUrl);
    }
  })();
  timingsInflight.set(timingsUrl, p);
  return p;
}

// --- Name decoration (unchanged) ---
const NAME_PATTERNS: Array<(name: string, text: string) => string> = [
  (n, t) => `${n}, ${lowerFirst(t)}`,
  (n, t) => `Listen carefully, ${n}. ${t}`,
  (n, t) => `${t} You hear me, ${n}?`,
  (n, t) => `Now ${n}, ${lowerFirst(t)}`,
  (n, t) => `${t} That's for you, ${n}.`,
];
function lowerFirst(s: string): string {
  if (!s) return s;
  return s[0]!.toLowerCase() + s.slice(1);
}
export function decorateWithName(text: string, name?: string | null): string {
  if (!text) return text;
  const trimmed = (name ?? "").trim();
  if (!trimmed) return text;
  const first = trimmed.split(/\s+/)[0]!;
  if (new RegExp(`\\b${escapeRegExp(first)}\\b`, "i").test(text)) return text;
  if (Math.random() > 0.55) return text;
  const pattern = NAME_PATTERNS[Math.floor(Math.random() * NAME_PATTERNS.length)]!;
  return pattern(first, text);
}
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// V3+ Voice source descriptor. Used by VoiceButton's source-aware path to
// drive the new `/api/tts/source` route. Mirrors the server's
// `VoiceableSourceRequest` shape but drops the access-control fields the
// server reconstructs from the session.
export interface VoiceSource {
  sourceType: "clue" | "inbox_message" | "mission_narrative";
  sourceId: number;
}

// --- FIFO playback queue ---
interface QueueItem {
  id: number;
  voice: string;
  text: string;
  direction: string;
  // When set, the queue processor uses fetchAudioForSource() instead of
  // fetchAudio(). The text field is still kept for log fingerprints + cache
  // dedup, but is NEVER sent to the server (the source resolver owns the
  // text bytes).
  source: VoiceSource | null;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (err: unknown) => void;
  cancelled: boolean;
  endNow: (() => void) | null;
}

const queue: QueueItem[] = [];
let processing = false;
let currentItem: QueueItem | null = null;
let nextItemId = 0;

async function processQueue(): Promise<void> {
  if (processing) return;
  processing = true;
  try {
    // Always wait for the iOS unlock dance to finish before touching <audio>.
    await unlockDone;
    while (queue.length > 0) {
      const item = queue.shift()!;
      audioLog("dequeue", `#${item.id} ${item.voice} (remaining ${queue.length}${item.cancelled ? ", cancelled" : ""})`);
      if (item.cancelled) {
        item.onEnd?.();
        continue;
      }
      currentItem = item;
      try {
        await playOne(item);
      } catch (err) {
        audioLog("queue-item-failed", err, "warn");
        item.onEnd?.();
      } finally {
        currentItem = null;
      }
    }
  } finally {
    processing = false;
  }
}

function playOne(item: QueueItem): Promise<void> {
  return new Promise<void>((resolve) => {
    const audio = getSharedAudio();
    let started = false;
    let settled = false;

    const settle = () => {
      if (settled) return;
      settled = true;
      audio.removeEventListener("playing", onPlaying);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      item.endNow = null;
      item.onEnd?.();
      resolve();
    };
    const onPlaying = () => {
      started = true;
      audioLog("playing", `#${item.id} ${item.voice}`);
      item.onStart?.();
    };
    const onEnded = () => {
      audioLog("ended", `#${item.id} ${item.voice} (queue ${queue.length})`);
      settle();
    };
    const onError = () => {
      audioLog("error", audio.error ? `code ${audio.error.code}` : "unknown", "warn");
      settle();
    };

    item.endNow = () => {
      try { audio.pause(); } catch {}
      settle();
    };

    audio.addEventListener("playing", onPlaying);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);

    const fetcher = item.source
      ? fetchAudioForSource(item.source, item.voice)
      : fetchAudio(item.voice, item.text, item.direction);
    fetcher
      .then((url) => {
        if (item.cancelled || settled) {
          settle();
          return;
        }
        realClipQueued = true;
        audioLog("src-assigned", `#${item.id} ${item.voice} (${url.startsWith("blob:") ? "blob" : "http"})`);
        audio.src = url;
        const p = audio.play();
        if (p && typeof p.catch === "function") {
          p.catch((err) => {
            audioLog("denied", err, "warn");
            if (!started) settle();
          });
        }
      })
      .catch((err) => {
        audioLog("fetch-failed", err, "warn");
        // Surface to the caller so the UI can branch on rate-limited
        // vs. generic error before the queue tears down. We invoke
        // onError BEFORE settle() so the button's onError handler
        // sees the failure ahead of the onEnd reset.
        try {
          item.onError?.(err);
        } catch (cbErr) {
          audioLog("onerror-threw", cbErr, "warn");
        }
        settle();
      });
  });
}

export interface SpeakOpts {
  onStart?: () => void;
  onEnd?: () => void;
  // Fired BEFORE onEnd when the underlying fetch fails. Pass an
  // isSpeechRateLimitedError() check to discriminate the cap-exceeded
  // case from generic errors.
  onError?: (err: unknown) => void;
  direction?: string;
  // V3+ source-aware playback. When set, the queue processor calls
  // POST /api/tts/source with the (sourceType, sourceId, voice) tuple
  // and the server resolves the text. Leave undefined for legacy
  // text-driven playback (CoalieTakeover, dynamic elf reactions).
  source?: VoiceSource;
}

export function speakAs(
  voiceSlug: string | undefined,
  text: string,
  opts: SpeakOpts = {},
): SpeechHandle {
  if (typeof window === "undefined" || (!text && !opts.source)) {
    opts.onEnd?.();
    return { stop: () => {} };
  }
  const voice = (voiceSlug && voiceSlug.length > 0 ? voiceSlug : "alloy");
  const item: QueueItem = {
    id: ++nextItemId,
    voice,
    text,
    direction: opts.direction ?? "",
    source: opts.source ?? null,
    onStart: opts.onStart,
    onEnd: opts.onEnd,
    onError: opts.onError,
    cancelled: false,
    endNow: null,
  };
  queue.push(item);
  audioLog("enqueue", `#${item.id} ${voice} (queue ${queue.length}${currentItem ? ", playing" : ""})`);
  // Kick the processor — it's a no-op if it's already running.
  processQueue();

  return {
    stop: () => {
      if (item.cancelled) return;
      item.cancelled = true;
      // If this item is currently playing, fire ended manually so the
      // queue advances. Otherwise it's still pending — the processor will
      // see cancelled=true when it dequeues and skip it (still firing onEnd).
      if (currentItem === item && item.endNow) {
        audioLog("stop", `#${item.id} (current)`);
        item.endNow();
      } else {
        audioLog("stop", `#${item.id} (queued)`);
      }
    },
  };
}

// Skip whatever's playing right now. Pending queued items continue normally.
export function skipCurrent(): void {
  if (currentItem && currentItem.endNow) {
    audioLog("skipCurrent", `#${currentItem.id}`);
    currentItem.endNow();
  }
}

// Drain the entire queue, including the current clip. Useful on route exits.
export function stopAll(): void {
  audioLog("stopAll", `queue ${queue.length}${currentItem ? ", playing" : ""}`);
  for (const item of queue) {
    item.cancelled = true;
  }
  if (currentItem && currentItem.endNow) {
    currentItem.cancelled = true;
    currentItem.endNow();
  }
}

// --- Gesture tracking + iOS audio unlock ---
// On the first user gesture we both (a) flip the hasGesture flag and
// (b) "unlock" the shared <audio> element by calling play() on a silent
// data-URL while we still hold the gesture. After this, the same element can
// be programmatically re-played later without iOS denying the call.
let hasGesture = false;
const SILENT_MP3 =
  // 0.05s of silence — minimal valid MP3 frame.
  "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjQ1LjEwMAAAAAAAAAAAAAAA/+M4wAAAAAAAAAAAAEluZm8AAAAPAAAAAwAAAbAAuLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uLi4uP/7kmQAAAB1AHmcAQAIAACUAQAAAEgGgAA0AAAAAAANIAAAAQE";

if (typeof window !== "undefined") {
  const unlock = () => {
    hasGesture = true;
    audioLog("unlock-start");
    let resolveUnlock!: () => void;
    unlockDone = new Promise<void>((r) => {
      resolveUnlock = r;
    });
    try {
      const a = getSharedAudio();
      a.muted = true;
      a.src = SILENT_MP3;
      const p = a.play();
      let settled = false;
      const finishUnlock = (reason: string) => {
        if (settled) return;
        settled = true;
        try {
          // CRITICAL: if speakAs() has already queued a real clip in the
          // same click handler, our pause/removeAttribute/load() would rip
          // its src right out from under it. Skip the cleanup in that case.
          if (realClipQueued) {
            audioLog("unlock-skipped", "real clip queued");
            a.muted = false;
          } else {
            audioLog("unlock-ran", reason);
            a.pause();
            a.muted = false;
            a.removeAttribute("src");
            a.load();
          }
        } catch {}
        resolveUnlock();
      };
      // HARD TIMEOUT: on iOS Safari, a.play() on a data: URL sometimes
      // returns a Promise that NEVER settles (no resolve, no reject). That
      // would deadlock the entire queue (which awaits unlockDone before
      // every clip). Force the unlock to "complete" after 800ms so playback
      // proceeds even if the silent prime never finished. The element has
      // still been touched inside the gesture, which is the part iOS cares
      // about for subsequent play() calls on the same element.
      const timeoutId = setTimeout(() => finishUnlock("timeout"), 800);
      const wrap = (reason: string) => {
        clearTimeout(timeoutId);
        finishUnlock(reason);
      };
      if (p && typeof p.then === "function") {
        p.then(() => wrap("resolved")).catch((err) => {
          audioLog("unlock-play-rejected", err, "warn");
          wrap("rejected");
        });
      } else {
        wrap("sync");
      }
    } catch (err) {
      audioLog("unlock-threw", err, "warn");
      resolveUnlock();
    }
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
    window.removeEventListener("touchstart", unlock);
    window.removeEventListener("click", unlock);
  };
  window.addEventListener("pointerdown", unlock, { once: true });
  window.addEventListener("keydown", unlock, { once: true });
  window.addEventListener("touchstart", unlock, { once: true });
  window.addEventListener("click", unlock, { once: true });
}
export function userHasInteracted(): boolean {
  return hasGesture;
}

// Subscribe to the very next user gesture. Useful for retrying autoplay if
// it tried to fire before the user had tapped anywhere. Returns an unsub.
const gestureWaiters: Array<() => void> = [];
export function onFirstGesture(cb: () => void): () => void {
  if (hasGesture) {
    cb();
    return () => {};
  }
  gestureWaiters.push(cb);
  return () => {
    const i = gestureWaiters.indexOf(cb);
    if (i >= 0) gestureWaiters.splice(i, 1);
  };
}
if (typeof window !== "undefined") {
  const fire = () => {
    while (gestureWaiters.length) {
      try {
        gestureWaiters.shift()!();
      } catch {}
    }
  };
  // These run AFTER the unlock() handlers above (because unlock uses
  // { once: true } and was registered first). Use capture: false default.
  window.addEventListener("pointerdown", fire);
  window.addEventListener("keydown", fire);
  window.addEventListener("touchstart", fire);
  window.addEventListener("click", fire);
}

// Pre-fetch a TTS audio URL into the in-memory blob cache so the eventual
// <audio> playback is guaranteed-playable (blob: URLs always work, no
// CORS / credential surprises). Safe to call multiple times.
//
// IMPORTANT: registerAudioUrl() may have already populated the cache with
// an HTTP URL (e.g. /api/tts/<key>). A naive fetchAudio() would see the
// cache hit and return without ever downloading bytes. So we explicitly
// download the HTTP URL into a blob: URL and overwrite the cache entry.
export async function prefetchAudio(
  voice: string,
  text: string,
  direction = "",
): Promise<void> {
  const key = cacheKey(voice, text, direction);
  const existing = blobCache.get(key);
  audioLog("prefetch-start", `${voice} (${existing ? (existing.startsWith("blob:") ? "blob" : "http") : "miss"})`);
  // Already a blob URL — nothing to do.
  if (existing && existing.startsWith("blob:")) {
    audioLog("prefetch-complete", "already blob");
    return;
  }
  try {
    if (existing) {
      // Have an HTTP URL from registerAudioUrl — download its bytes.
      const blobUrl = await downloadToBlob(key, existing);
      audioLog("prefetch-complete", `${voice} (${blobUrl.startsWith("blob:") ? "blob" : "http"})`);
    } else {
      // No URL registered — fall back to a fresh POST /tts.
      await fetchAudio(voice, text, direction);
      audioLog("prefetch-complete", "via POST");
    }
  } catch (err) {
    // Keep the existing HTTP URL as a fallback so taps still work, just
    // without the pre-buffered guarantee.
    audioLog("prefetch-failed", err, "warn");
  }
}
